// The tester's view of a generated suite: pages first, then that page's cases,
// then one case in full.
//
// This replaces two side-by-side cards — an unbounded list of crawled pages, and
// a separate case list that couldn't be related to it. You could see that
// /admin/ai had 6 tests and, separately, read a stream of cases, but you could
// not get from one to the other, and you could not act on a page. The whole
// point of the page breakdown is "test just this bit", so the page row is now
// where the send buttons live.
//
// Everything counts and filters on the SERVER. In particular the page a row
// represents is matched by the backend's canonical URL rule, so a row that says
// 11 cases sends exactly those 11 — grouping and pushing used to disagree.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft, Bot, Puzzle, Loader2, FileText, Globe, DollarSign, Clock,
  CheckCircle2, AlertTriangle, X, Hand, Download,
} from "lucide-react";
import DataTable from "@/components/dashboard/DataTable";
import CaseDetailModal from "@/components/shared/CaseDetailModal";
import Button from "@/components/ui/Button";
import {
  downloadProjectTestCases, pageProjectTestCases, pushToExe, pushToExtension,
} from "@/api/projects";
import { cn } from "@/lib/cn";

const CASE_PAGE_SIZE = 20;
const PAGE_SIZE = 12;

const CATEGORY_STYLE = {
  functional: "bg-brand-500/10 text-brand-600 dark:text-brand-300",
  navigation: "bg-sky-500/10 text-sky-600 dark:text-sky-300",
  edge_case: "bg-amber-500/10 text-amber-600 dark:text-amber-300",
  ui: "bg-teal-500/10 text-teal-600 dark:text-teal-300",
};

// Same rule the server uses for the page-level "N manual-only" badge: anything
// that isn't "auto" needs a human, so the two can never disagree.
const isAuto = (c) => String(c?.execution_mode || "auto").toLowerCase() === "auto";

const fmtCost = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
const fmtTime = (v) => {
  if (v == null) return "—";
  const s = Math.round(v);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};

/** Turn a push response into something a human can act on.
 *
 * The API has always returned pushed/duplicates/skipped and the UI has always
 * thrown it away, so a second click looked identical to the first. Saying "3
 * already there" is what makes re-sending feel safe rather than destructive.
 */
function pushSummary(res, where) {
  const bits = [`${res.pushed || 0} sent to ${where}`];
  if (res.duplicates) bits.push(`${res.duplicates} already there`);
  if (res.skipped) bits.push(`${res.skipped} skipped`);
  return bits.join(" · ");
}

function Stat({ icon: Icon, label, value, tone }) {
  // `card` — the app's shared surface (white, soft shadow, real border). These
  // were a bg-slate-50/70 tint, which on the page's own grey backdrop rendered
  // as barely-there boxes floating in the layout.
  return (
    <div className="card flex items-center gap-3 px-4 py-3">
      <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl", tone)}>
        <Icon size={17} />
      </span>
      <div className="min-w-0">
        <p className="truncate text-xs font-medium uppercase tracking-wide text-slate-400">
          {label}
        </p>
        <p className="text-xl font-bold text-slate-900 dark:text-white">{value}</p>
      </div>
    </div>
  );
}

/** The send buttons, wherever they appear (a page row, or a selection).
 *
 * `onDownload` is optional and only passed where the export endpoint can express
 * the same scope — i.e. a page. An arbitrary case SELECTION has no equivalent
 * server-side filter, so that bar keeps the two send buttons only rather than
 * offering a download that would quietly hand back the wrong rows.
 *
 * Download stays enabled when `disabled` is set: `disabled` means "nothing here
 * can be AUTOMATED", and a page of manual-only cases is exactly the one you most
 * want in a spreadsheet.
 */
function SendButtons({ label, disabled, busyTarget, onSend, onDownload, downloading, size = "sm" }) {
  return (
    <div className="flex shrink-0 gap-1.5" onClick={(e) => e.stopPropagation()}>
      {onDownload && (
        <Button
          variant="outline" size={size} disabled={downloading}
          onClick={onDownload}
          title={`Download ${label} as Excel`}
        >
          {downloading
            ? <Loader2 size={14} className="animate-spin" />
            : <Download size={14} />}
          Download
        </Button>
      )}
      <Button
        variant="outline" size={size} disabled={disabled || !!busyTarget}
        onClick={() => onSend("exe")}
        title={disabled ? "Nothing here can be run automatically" : `Send ${label} to the Exe agent`}
      >
        {busyTarget === "exe"
          ? <Loader2 size={14} className="animate-spin" />
          : <Bot size={14} />}
        Exe
      </Button>
      <Button
        variant="outline" size={size} disabled={disabled || !!busyTarget}
        onClick={() => onSend("extension")}
        title={disabled ? "Nothing here can be run automatically" : `Send ${label} to the Extension`}
      >
        {busyTarget === "extension"
          ? <Loader2 size={14} className="animate-spin" />
          : <Puzzle size={14} />}
        Extension
      </Button>
    </div>
  );
}

/**
 * @param {string} projectId
 * @param {number} total       project-wide case count (from the summary call)
 * @param {array}  byPage      [{page_url, page_key, count, functional, navigation,
 *                              edge_case, automatable}] — already canonical
 * @param {object} generation  latest generation summary, or null
 */
export default function ProjectCasesCard({ projectId, total, byPage = [], generation }) {
  // "By page" is the default because the question is almost always "what covers
  // this screen?" — the flat list is kept for when you only remember the wording.
  const [tab, setTab] = useState("pages");
  // The page being drilled into, as its canonical key. Mirrored into the URL so
  // a refresh or a shared link lands back here.
  const [openPage, setOpenPage] = useState(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("page");
  });

  const [casePage, setCasePage] = useState(1);
  const [criteria, setCriteria] = useState({ query: "", filters: {} });
  const [cases, setCases] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [openCase, setOpenCase] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busyTarget, setBusyTarget] = useState(null);
  const [rowBusy, setRowBusy] = useState({});      // page_key -> "exe" | "extension"
  const [rowDl, setRowDl] = useState(null);        // page_key currently downloading
  const [toast, setToast] = useState(null);

  const page = useMemo(
    () => byPage.find((p) => p.page_key === openPage) || null,
    [byPage, openPage],
  );

  function say(kind, message) {
    setToast({ kind, message });
    setTimeout(() => setToast(null), 6000);
  }

  // Keep ?page= in sync without adding a history entry per click — the back
  // button should leave the project, not walk back through pages you browsed.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (openPage) url.searchParams.set("page", openPage);
    else url.searchParams.delete("page");
    window.history.replaceState({}, "", url);
  }, [openPage]);

  const loadCases = useCallback(async () => {
    if (!total) { setCases({ rows: [], total: 0 }); return; }
    setLoading(true);
    try {
      const res = await pageProjectTestCases(projectId, {
        limit: CASE_PAGE_SIZE,
        offset: (casePage - 1) * CASE_PAGE_SIZE,
        search: criteria.query || undefined,
        category: criteria.filters.category !== "all" ? criteria.filters.category : undefined,
        // Drilled in: scope to that page. The backend resolves the canonical key
        // back to every raw URL that means the same page.
        page_url: page ? page.page_url : undefined,
      });
      setCases({ rows: res.test_cases || [], total: res.filtered_total ?? res.total ?? 0 });
    } catch (e) {
      setCases({ rows: [], total: 0 });
      say("error", (e && e.message) || "Could not load the test cases.");
    } finally {
      setLoading(false);
    }
  }, [projectId, casePage, criteria, page, total]);

  // Only the case views need rows; the page table renders from `byPage`.
  useEffect(() => {
    if (tab === "pages" && !openPage) return;
    loadCases();
  }, [tab, openPage, loadCases]);

  // Changing what you're looking at resets the page and the selection —
  // carrying a selection across a filter change is how you send the wrong cases.
  useEffect(() => { setCasePage(1); setSelected(new Set()); }, [openPage, tab]);

  async function send(target, scope, label, rowKey) {
    const fn = target === "exe" ? pushToExe : pushToExtension;
    if (rowKey) setRowBusy((b) => ({ ...b, [rowKey]: target }));
    else setBusyTarget(target);
    try {
      const res = await fn(projectId, scope);
      say("success", pushSummary(res, target === "exe" ? "the Exe agent" : "the Extension"));
      setSelected(new Set());
    } catch (e) {
      say("error", (e && e.message) || `Could not send ${label}.`);
    } finally {
      if (rowKey) setRowBusy((b) => ({ ...b, [rowKey]: undefined }));
      else setBusyTarget(null);
    }
  }

  // Excel of just this page's cases. Same canonical page_url the counts and the
  // send buttons use, so the workbook holds exactly the rows the row claims.
  async function downloadPage(p) {
    setRowDl(p.page_key);
    try {
      await downloadProjectTestCases(projectId, { pageUrl: p.page_url });
    } catch (e) {
      say("error", (e && e.message) || `Could not download the cases on ${p.page_url}.`);
    } finally {
      setRowDl(null);
    }
  }

  // ---- columns -------------------------------------------------------------
  const pageColumns = [
    {
      key: "page_url", header: "Page",
      render: (p) => (
        <div className="min-w-0">
          <p className="line-clamp-1 max-w-md font-mono text-xs text-slate-600 dark:text-slate-300"
             title={p.page_url}>
            {p.page_url}
          </p>
          {p.automatable < p.count && (
            <p className="mt-0.5 flex items-center gap-1 text-[0.6875rem] text-amber-600 dark:text-amber-400">
              <Hand size={10} /> {p.count - p.automatable} manual-only
            </p>
          )}
        </div>
      ),
    },
    { key: "functional", header: "Functional", align: "center", sortable: true },
    { key: "navigation", header: "Navigation", align: "center", sortable: true },
    { key: "edge_case", header: "Edge case", align: "center", sortable: true },
    {
      key: "count", header: "Total", align: "center", sortable: true,
      render: (p) => (
        <span className="rounded-full bg-brand-500/10 px-2.5 py-0.5 text-xs font-semibold text-brand-600 dark:text-brand-300">
          {p.count}
        </span>
      ),
    },
    {
      key: "actions", header: "Run this page", align: "right",
      render: (p) => (
        <div className="flex justify-end">
          <SendButtons
            label={`this page's ${p.automatable} cases`}
            // A page of manual-only cases would 400 on the server; better to say
            // so on the button than after the click.
            disabled={!p.automatable}
            busyTarget={rowBusy[p.page_key]}
            onSend={(target) => send(
              target, { pageUrl: p.page_url }, `the cases on ${p.page_url}`, p.page_key)}
            onDownload={() => downloadPage(p)}
            downloading={rowDl === p.page_key}
          />
        </div>
      ),
    },
  ];

  const caseColumns = [
    {
      key: "category", header: "Type", sortable: true,
      render: (c) => (
        <span className={cn(
          "inline-block whitespace-nowrap rounded-md px-2 py-0.5 text-[0.625rem] font-semibold uppercase",
          CATEGORY_STYLE[c.category] || CATEGORY_STYLE.functional)}>
          {String(c.category || "functional").replace(/_/g, " ")}
        </span>
      ),
    },
    {
      key: "execution_mode", header: "Run mode",
      render: (c) => {
        const auto = isAuto(c);
        return (
          <span
            // The reason is the useful half: "manual" on its own just raises the
            // question. It is already on the row, so hovering answers it without
            // opening the case.
            title={auto
              ? "Runs unattended in Run all / Exe / Extension"
              : c.manual_reason || "A tester has to run this one"}
            className={cn(
              "inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-0.5",
              "text-[0.625rem] font-semibold uppercase",
              auto
                ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300"
                : "bg-amber-500/10 text-amber-600 dark:text-amber-300")}>
            {auto ? <Bot size={10} /> : <Hand size={10} />}
            {auto ? "Auto" : "Manual"}
          </span>
        );
      },
    },
    {
      key: "instruction", header: "Test case",
      render: (c) => (
        <p className="line-clamp-2 max-w-2xl text-slate-700 dark:text-slate-200">
          {c.instruction}
        </p>
      ),
    },
    // Inside a page every row shares the same URL, so showing it is just noise.
    ...(page ? [] : [{
      key: "page_url", header: "Page",
      render: (c) => (
        <span className="line-clamp-1 max-w-[16rem] font-mono text-xs text-slate-400"
              title={c.page_url}>
          {c.page_url}
        </span>
      ),
    }]),
  ];

  const caseFilters = [
    { key: "category", label: "types",
      options: ["functional", "navigation", "edge_case", "ui"] },
  ];

  const serverPaging = {
    total: cases.total,
    page: casePage,
    loading,
    onChange: ({ page: p, query, filters }) => {
      setCasePage(p);
      setCriteria((prev) =>
        prev.query === query && JSON.stringify(prev.filters) === JSON.stringify(filters)
          ? prev
          : { query, filters });
    },
  };

  const selectionBar = selected.size > 0 && (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-brand-500/10 px-4 py-2.5">
      <span className="text-sm font-medium text-brand-800 dark:text-brand-200">
        {selected.size} case{selected.size === 1 ? "" : "s"} selected
      </span>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setSelected(new Set())}
                className="text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400">
          Clear
        </button>
        <SendButtons
          label={`${selected.size} selected`}
          busyTarget={busyTarget}
          onSend={(target) => send(
            target, { caseIds: [...selected] }, `${selected.size} selected cases`)}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* what exists, what it covers, what it cost */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={FileText} label="Test cases" value={total.toLocaleString()}
              tone="text-brand-500 bg-brand-500/10" />
        <Stat icon={Globe} label="Pages covered" value={byPage.length}
              tone="text-sky-500 bg-sky-500/10" />
        <Stat icon={DollarSign} label="Generation cost"
              value={generation ? fmtCost(generation.cost_usd) : "—"}
              tone="text-emerald-500 bg-emerald-500/10" />
        <Stat icon={Clock} label="Time taken"
              value={generation ? fmtTime(generation.duration_seconds) : "—"}
              tone="text-amber-500 bg-amber-500/10" />
      </div>

      {toast && (
        <div className={cn(
          "flex items-start gap-2.5 rounded-xl px-4 py-3 text-sm",
          toast.kind === "error"
            ? "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300"
            : "bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200")}>
          {toast.kind === "error"
            ? <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            : <CheckCircle2 size={16} className="mt-0.5 shrink-0" />}
          <span className="flex-1">{toast.message}</span>
          <button type="button" onClick={() => setToast(null)} className="opacity-60 hover:opacity-100">
            <X size={14} />
          </button>
        </div>
      )}

      {/* tabs / breadcrumb */}
      {openPage ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <button type="button" onClick={() => setOpenPage(null)}
                    className="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-brand-600 dark:text-slate-400">
              <ArrowLeft size={15} /> All pages
            </button>
            <span className="text-slate-300 dark:text-slate-600">/</span>
            <span className="line-clamp-1 font-mono text-xs text-slate-600 dark:text-slate-300"
                  title={page?.page_url}>
              {page?.page_url || openPage}
            </span>
          </div>
          {page && (
            <SendButtons
              label={`this page's ${page.automatable} cases`}
              disabled={!page.automatable}
              busyTarget={busyTarget}
              onSend={(target) => send(
                target, { pageUrl: page.page_url }, `the cases on ${page.page_url}`)}
              onDownload={() => downloadPage(page)}
              downloading={rowDl === page.page_key}
            />
          )}
        </div>
      ) : (
        // Bordered for the same reason as the stat tiles: a bare slate-100 strip
        // on the page's grey backdrop reads as a gap, not as a control.
        <div className="inline-flex rounded-xl border border-slate-200/80 bg-slate-100 p-1 dark:border-white/10 dark:bg-white/5">
          {[
            { key: "pages", label: `By page (${byPage.length})` },
            { key: "all", label: `All cases (${total.toLocaleString()})` },
          ].map((t) => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
              className={cn(
                "rounded-lg px-4 py-1.5 text-sm font-medium transition-colors",
                tab === t.key
                  ? "bg-white text-slate-900 shadow-soft dark:bg-white/10 dark:text-white"
                  : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white")}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {selectionBar}

      {openPage || tab === "all" ? (
        <DataTable
          columns={caseColumns}
          rows={cases.rows}
          searchKeys={["instruction", "page_url"]}
          filters={caseFilters}
          pageSize={CASE_PAGE_SIZE}
          maxHeight="32rem"
          onRowClick={setOpenCase}
          selectable
          selectedIds={selected}
          onSelectionChange={setSelected}
          emptyLabel={loading ? "Loading…" : "No cases match those filters."}
          server={serverPaging}
        />
      ) : (
        <DataTable
          columns={pageColumns}
          rows={byPage}
          getRowId={(p) => p.page_key}
          searchKeys={["page_url"]}
          pageSize={PAGE_SIZE}
          maxHeight="32rem"
          initialSort={{ key: "count", dir: "desc" }}
          onRowClick={(p) => setOpenPage(p.page_key)}
          emptyLabel="No pages crawled yet."
        />
      )}

      <CaseDetailModal
        testCase={openCase}
        kind="cloud"
        projectId={projectId}
        onClose={() => setOpenCase(null)}
      />
    </div>
  );
}
